import React, { useMemo, forwardRef, useRef, useImperativeHandle } from 'react';
import { TablePaginatedV8, order2Sorting, TableCellV8, CellDateV8 } from 'motion-components';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { Col, Row } from 'react-bootstrap';

/* ----------- Grid ------------------ */

export const GridPage = forwardRef((props: any, ref) => {
  const {
    data,
    sumData,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;

  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const totalNetWeight = useMemo(() => {
    if (!data) return 0;
    return data.reduce((sum, row) => {
      if (!row.collectionLines) return sum;
      return (
        sum + row.collectionLines.reduce((lineSum, line) => lineSum + (line.netWeight || 0), 0)
      );
    }, 0);
  }, [data]);

  const columns = useMemo(
    () => [
      {
        id: 'code',
        header: t('Κωδικός Συλλογής'),
        columnName: t('Κωδικός Συλλογής'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: ({ row }) => {
          const path = generatePath(getRouteByName('Collection'), { id: row.original.id ?? 0 });
          return (
            <div>
              <Link to={path}>{row.original.code}</Link>
            </div>
          );
        },
        size: 20,
        footer: () => {
          if (!data || data.length === 0) return null;
          return (
            <div className="text-left">
              {t('Σύνολο εγγραφών')}: <span style={{ fontWeight: 800 }}>{data.length}</span>
            </div>
          );
        },
      },
      {
        id: 'orderedDate',
        header: t('Ημερομηνία Συλλογής'),
        columnName: t('Ημερομηνία Συλλογής'),
        accessorKey: 'dateCollected',
        orderByField: 'dateCollected',
        cell: CellDateV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'collectorId',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        orderByField: 'collector.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'wastereceptionpointId',
        header: t('Προορισμός'),
        columnName: t('Προορισμός'),
        accessorKey: 'wastereceptionpoint.name',
        orderByField: 'wastereceptionpoint.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'collectionLines',
        header: t('Απόβλητα'),
        columnName: t('Απόβλητα'),
        accessorKey: 'collectionLines',
        orderByField: 'collectionLines',
        cell: ({ getValue, row }) => {
          function compare(a, b) {
            if (a.aa < b.aa) {
              return -1;
            }
            if (a.aa > b.aa) {
              return 1;
            }
            return 0;
          }
          const lines = row.original.collectionLines;
          return (
            <div className="text-left">
              {lines.sort(compare).map((v, i) => {
                return (
                  <div key={i + v.aa}>
                    {v?.aa}: {v?.material?.name} ({v?.qty} ΤΕΜ) {v?.netWeight} kg
                  </div>
                );
              })}
            </div>
          );
        },
        size: 35,
        footer: () => {
          if (!totalNetWeight) return null;
          return (
            <div>
              {sumData &&
                sumData.map((ln, idx) => {
                  return (
                    <Row key={idx}>
                      <Col sm="9" className="text-left">
                        {ln.materialName}
                      </Col>
                      <Col sm="3" className="text-right">
                        {ln.netWeight} Kg
                      </Col>
                    </Row>
                  );
                })}
            </div>
          );
        },
      },
    ],
    [t, data, totalNetWeight, sumData],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          showFooter={true}
          showGridFooter={true}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
